/**
 * The document an inbox activity points at, in the Requests content column.
 *
 * A mention, a reply or a decision request names a document and a place in
 * it. The row used to open that document in a new browser tab, which left the
 * inbox behind (and, inside Nimbalyst's own browser, left the app). It now
 * renders here beside the list, the way a feedback request does: the reader
 * answers the comment where they found it, and the next row is still there.
 *
 * The document is the docs route's own editor -- comments, presence, and the
 * anchor that scrolls to the thread -- mounted over this project's docs scope,
 * because the editor for a document is chosen from the project's index.
 */

import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CollabDocsUIProvider,
  EditorBreadcrumb,
  EditorHeaderBar,
  useSharedDocumentTitles,
} from '@nimbalyst/collab-bundle/docs-ui';

import type { TeamSummary } from '../api';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { CollabLoadingPanel, PanelState } from '../components/Chrome';
import {
  CollabDocumentStatusIndicator,
  CollabDocumentStatusProvider,
  useCollabDocumentStatus,
} from '../collab/documentStatus';
import { useProjectDocsScope } from '../collab/useProjectDocsScope';
import { consoleConfig } from '../config';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { getProjectRouteId } from '../projectSelection';
import { orgRequestsPath } from '../routing';
import { SharedDocumentEditor } from '../routes/DocsRoutes';
import { requestDocumentPath, requestDocumentProject, type RequestRow } from './requestsInboxModel';

const EMPTY_COLUMN = 'flex h-full flex-col items-center justify-center gap-[3px] px-[1.6rem] py-8 text-center';

/** The content column when the row cannot be shown: gone, or not a document. */
export function RequestActivityUnavailable({
  orgName,
  title,
  detail,
}: {
  orgName: string;
  title: string;
  detail: string;
}) {
  return (
    <section className="request-activity-unavailable flex min-w-0 min-h-0 flex-col overflow-hidden">
      <EditorHeaderBar
        breadcrumb={(
          <EditorBreadcrumb
            crumbs={[
              { id: 'org', label: orgName },
              { id: 'requests', label: 'Requests', current: true },
            ]}
          />
        )}
      />
      <div className={`request-activity-unavailable-body min-h-0 flex-1 overflow-y-auto ${EMPTY_COLUMN}`}>
        <strong className="text-[0.86rem] font-semibold text-nim">{title}</strong>
        <p className="mt-1 max-w-[34ch] text-[0.76rem] leading-relaxed text-nim-faint">{detail}</p>
      </div>
    </section>
  );
}

function RequestActivityHeader({
  team,
  row,
  documentId,
}: {
  team: TeamSummary;
  row: RequestRow;
  documentId: string;
}) {
  const navigate = useNavigate();
  const status = useCollabDocumentStatus();
  const title = useSharedDocumentTitles().get(documentId) ?? 'Opening document…';
  const documentPath = requestDocumentPath(team, row);
  return (
    <EditorHeaderBar
      breadcrumb={(
        <EditorBreadcrumb
          crumbs={[
            { id: 'org', label: team.name },
            { id: 'requests', label: 'Requests', onClick: () => navigate(orgRequestsPath(team)) },
            { id: 'document', label: title, current: true },
          ]}
        />
      )}
      actions={(
        <>
          <CollabDocumentStatusIndicator status={status} />
          {documentPath && (
            <Link
              className="request-activity-open flex h-7 items-center gap-1 rounded px-1.5 text-[12px] text-nim-muted no-underline hover:bg-nim-hover hover:text-nim"
              to={documentPath}
              title="Open this document in Shared Docs"
            >
              <DecorativeIcon icon="description" size={16} />
              Open in Shared Docs
            </Link>
          )}
        </>
      )}
    />
  );
}

export function RequestActivityPane({
  team,
  teamSession,
  getTeamJwt,
  row,
}: {
  team: TeamSummary;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  row: RequestRow;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const project = useMemo(() => requestDocumentProject(team, row), [row, team]);
  // The team's own project record, which carries the fields the scope needs;
  // `requestDocumentProject` resolves against the same list, so this is a
  // lookup rather than a second decision.
  const teamProject = useMemo(
    () => (project ? team.projects.find((candidate) => candidate.projectId === project.projectId) ?? null : null),
    [project, team.projects],
  );
  const { session, error } = useProjectDocsScope({
    team,
    project: teamProject,
    teamSession,
    getTeamJwt,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    navigate: (path) => navigate(path),
  });

  const documentId = row.documentTarget?.documentId;
  if (!teamProject || !documentId) {
    return (
      <RequestActivityUnavailable
        orgName={team.name}
        title="This document is not available here"
        detail="It belongs to a project this console cannot open. The desktop app can show it."
      />
    );
  }
  if (error) {
    return (
      <PanelState tone="error" className="docs-state-panel m-8 w-full self-center" role="alert" data-docs-state="service-unavailable">
        <strong>Shared documents are unavailable</strong>
        <span>{error.message}</span>
      </PanelState>
    );
  }
  if (!session) {
    return (
      <CollabLoadingPanel
        title="Opening the document"
        detail="Connecting to your team’s live document index."
      />
    );
  }
  return (
    <CollabDocsUIProvider session={session}>
      <CollabDocumentStatusProvider>
        <section className="request-activity flex h-full min-w-0 min-h-0 flex-col overflow-hidden" data-delivery-id={row.deliveryId}>
          <RequestActivityHeader team={team} row={row} documentId={documentId} />
          <div className="request-activity-scroll min-h-0 flex-1 overflow-y-auto [&>*]:min-h-full">
            <SharedDocumentEditor
              projectId={getProjectRouteId(teamProject)}
              documentId={documentId}
              teamSession={teamSession}
              getTeamJwt={getTeamJwt}
              search={location.search}
            />
          </div>
        </section>
      </CollabDocumentStatusProvider>
    </CollabDocsUIProvider>
  );
}
